// firmenZentrale.ts — woher die Zentrale für die automatische Durchwahl einer
// PERSON kommt (Format „+49 221 12345-67", Logik: @kmvs/forms-core, telefon.ts).
//
// Bei einer Firma ist die Zentrale ihre eigene Telefonnummer. Bei einer
// geschäftlichen Nummer einer Person steht sie nicht im Formular — sie ergibt
// sich aus zwei Quellen:
//
// 1. Den Firmen der Person (Arbeitgeber, „Verbindung zu Unternehmen",
//    „Mensch im/Verantwortung fürs Unternehmen"): deren Telefon- und
//    Faxnummern, geladen über den vorhandenen Endpunkt
//    GET /contact/:id?include=communications (keine eigene Route). Je Firma
//    wird einmal geladen und für die Sitzung gemerkt.
// 2. Den anderen geschäftlichen Nummern derselben Person
//    (`zentraleAusNummern`: gemeinsamer Anfang bzw. ausdrückliche Durchwahl).
//
// Beides zusammen geht als `zentrale` an das Telefonfeld; die Durchwahl setzt
// dann wie immer erst das Muster beim Einfügen/Verlassen.

import * as React from "react";
import { zentraleAusNummern } from "@kmvs/forms-core";
import { contactCommunicationService } from "../../../services/contactCommunicationService";

type Komm = { type?: string | null; value?: string | null; isDefault?: boolean | null };

/**
 * Die Nummern einer Firma, die als Zentrale taugen: Telefon (Standardnummer
 * zuerst) und Fax — ein Fax „+49 221 12345-99" verrät die Zentrale ebenso.
 * Mobilnummern nicht: dahinter hängt keine Anlage.
 */
export function zentralenDerFirma(comms: readonly Komm[] | null | undefined): string[] {
  const liste = (comms ?? []).filter(
    (c) => (c?.type === "phone" || c?.type === "fax") && String(c?.value ?? "").trim() !== "",
  );
  const rang = (c: Komm) => (c.type === "phone" ? (c.isDefault ? 0 : 1) : 2);
  return [...liste].sort((a, b) => rang(a) - rang(b)).map((c) => String(c.value).trim());
}

const cache = new Map<number, Promise<string[]>>();

/** Zentralnummern einer Firma laden (einmal je Firma und Sitzung). Fehler → keine. */
export function firmenZentraleLaden(firmaId: number): Promise<string[]> {
  let p = cache.get(firmaId);
  if (!p) {
    p = contactCommunicationService
      .loadMeta(firmaId)
      .then((meta) => zentralenDerFirma(meta.communications))
      .catch(() => {
        // Kein Leserecht, gelöscht, offline: dann eben ohne Zentrale. Nicht
        // merken, damit ein späterer Versuch es erneut probieren kann.
        cache.delete(firmaId);
        return [];
      });
    cache.set(firmaId, p);
  }
  return p;
}

/** Nur für Tests: gemerkte Zentralen verwerfen. */
export function firmenZentraleCacheLeeren(): void {
  cache.clear();
}

const gueltigeIds = (ids: readonly (number | string | null | undefined)[]): number[] =>
  Array.from(
    new Set(
      ids
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0),
    ),
  ).sort((a, b) => a - b);

/** Zentralnummern der angegebenen Firmen (lädt nach, gecacht). */
export function useFirmenZentralen(firmaIds: readonly (number | string | null | undefined)[]): string[] {
  const ids = gueltigeIds(firmaIds);
  const schluessel = ids.join(",");
  const [zentralen, setZentralen] = React.useState<string[]>([]);
  React.useEffect(() => {
    if (!schluessel) {
      setZentralen([]);
      return;
    }
    let aktiv = true;
    void Promise.all(schluessel.split(",").map((id) => firmenZentraleLaden(Number(id)))).then((listen) => {
      if (aktiv) setZentralen(Array.from(new Set(listen.flat())));
    });
    return () => {
      aktiv = false;
    };
  }, [schluessel]);
  return zentralen;
}

/**
 * Zentrale(n) für die geschäftlichen Festnetz-/Faxnummern einer Person.
 *
 * @param firmaIds  Firmen der Person (Arbeitgeber, Verbindungen) — deren Nummern werden geladen.
 * @param firmenNummern  Schon bekannte Firmennummern (z. B. aus einer neu eingetippten Firma).
 * @param eigeneNummern  Alle geschäftlichen Festnetz-/Faxnummern der Person (auch die des Feldes selbst).
 */
export function usePersonZentrale({
  firmaIds = [],
  firmenNummern = [],
  eigeneNummern = [],
}: {
  firmaIds?: readonly (number | string | null | undefined)[];
  firmenNummern?: readonly (string | null | undefined)[];
  eigeneNummern?: readonly (string | null | undefined)[];
}): string[] {
  const geladen = useFirmenZentralen(firmaIds);
  const firmenSchluessel = JSON.stringify(firmenNummern);
  const eigeneSchluessel = JSON.stringify(eigeneNummern);
  return React.useMemo(
    () => {
      const bekannte = firmenNummern.filter((n): n is string => typeof n === "string" && n.trim() !== "");
      return Array.from(new Set([...geladen, ...bekannte, ...zentraleAusNummern(eigeneNummern)]));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geladen, firmenSchluessel, eigeneSchluessel],
  );
}
